import React, { useRef, useEffect, useState } from "react";

// Convert SharePoint direct video URL to embed format
function convertToEmbedUrl(url) {
  if (!url) return url;
  
  // If it's already an embed URL, return as-is
  if (url.includes('/_layouts/15/embed.aspx')) {
    return url;
  }
  
  // If it's a direct video URL (:v:/g/...), we can't embed it directly
  // Instead, we'll show a message and provide a link to open it
  if (url.includes(':v:/g/')) {
    return null; // This will trigger the "not embeddable" message
  }
  
  return url;
}

export default function SharePointEmbed({
  src,
  title = "Course recording",
}) {
  const ref = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [embedUrl, setEmbedUrl] = useState(null);
  const [tookTooLong, setTookTooLong] = useState(false);

  // Convert URL to embed format
  useEffect(() => {
    const convertedUrl = convertToEmbedUrl(src);
    setEmbedUrl(convertedUrl);
    console.log('🔗 Original URL:', src);
    console.log('🔗 Converted embed URL:', convertedUrl);
  }, [src]);

  // Handle iframe load events
  useEffect(() => {
    if (!embedUrl) {
      setLoading(false);
      return;
    }
    
    const iframe = ref.current;
    if (!iframe) return;

    // Reset states when URL changes
    setLoading(true);
    setError(null);
    setTookTooLong(false);

    const handleLoad = () => {
      console.log('✅ Iframe loaded successfully');
      setLoading(false);
      setError(null);
    };

    const handleError = () => {
      console.log('❌ Iframe failed to load');
      setLoading(false);
      setError("Failed to load SharePoint video");
    };

    iframe.addEventListener('load', handleLoad);
    iframe.addEventListener('error', handleError);

    // Give mobile Safari more time to authorize media
    const timeout = setTimeout(() => {
      console.log('⏰ Iframe load timeout (10s)');
      setLoading(false);
      setTookTooLong(true);
    }, 10000); // 10 second timeout

    return () => {
      iframe.removeEventListener('load', handleLoad);
      iframe.removeEventListener('error', handleError);
      clearTimeout(timeout);
    };
  }, [embedUrl]);

  return (
    <div style={{ maxWidth: "640px" }}>
      <div style={{ 
        position: "relative", 
        paddingBottom: "56.25%", 
        height: 0, 
        overflow: "hidden",
        backgroundColor: "var(--video-background, #000)"
      }}>
        {loading && (
          <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "var(--text-inverse)",
            fontSize: "16px",
            textAlign: "center"
          }}>
            Loading SharePoint video...
          </div>
        )}
        
        {(error || !embedUrl) && (
          <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "var(--text-inverse)",
            fontSize: "16px",
            textAlign: "center",
            padding: "20px"
          }}>
            <div style={{ marginBottom: "10px" }}>
              {!embedUrl ? "📹" : "⚠️"} 
              {!embedUrl ? " Video Preview Not Available" : error}
            </div>
            <div style={{ fontSize: "14px", color: "var(--text-muted)", marginBottom: "15px" }}>
              {!embedUrl 
                ? "This is a direct video link that cannot be embedded. Click below to watch the video."
                : "This video may require authentication or the link may be incorrect."
              }
            </div>
            <button
              onClick={() => window.open(src, '_blank')}
              style={{
                padding: "12px 24px",
                backgroundColor: "var(--info-color)",
                color: "var(--text-inverse)",
                border: "none",
                borderRadius: 0,
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "500"
              }}
            >
              🎬 Watch Video
            </button>
          </div>
        )}

        {embedUrl && (
          <iframe
            ref={ref}
            key={embedUrl}
            src={embedUrl}
            width="640"
            height="360"
            frameBorder="0"
            scrolling="no"
            allow="autoplay; fullscreen; encrypted-media; picture-in-picture; clipboard-write"
            allowFullScreen
            loading="eager"
            title={title}
            style={{
              border: "none",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              height: "100%",
              maxWidth: "100%",
              opacity: loading ? 0 : 1,
              transition: "opacity 0.3s ease"
            }}
          />
        )}
      </div>

      {/* Mobile fallback if it keeps loading */}
      {tookTooLong && (
        <div style={{ marginTop: 12, textAlign: "center" }}>
          <button
            onClick={() => window.open(src, '_blank')}
            style={{
              padding: "10px 16px",
              backgroundColor: "var(--info-color)",
              color: "var(--text-inverse)",
              border: "none",
              borderRadius: 0,
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 500
            }}
          >
            Open in new tab
          </button>
        </div>
      )}
    </div>
  );
}
